/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Box, FlexContainer, Spacer, theme } from "@nordcloud/gnui";
import {
  OsType,
  ResourcesSortFields,
  SortOrder,
  CloudAccountCloudProvider,
} from "~/generated/graphql";
import { SortSelector } from "~/components/SortSelector";
import { TABLE_LABELS } from "~/constants";
import { useQueryState } from "~/hooks";
import { ResourceQuery } from "../types";
import { Actions } from "./Actions";
import { ResourcesFiltersSidebar } from "./ResourcesFiltersSidebar/ResourcesFiltersSidebar";
import { ResourceSearch } from "./ResourcesSearch/ResourceSearch";

const sortOptions = [
  {
    value: ResourcesSortFields.Name,
    label: "Resource Name",
  },
  {
    value: ResourcesSortFields.CreatedAt,
    label: TABLE_LABELS.createdAt,
  },
];

type Props = {
  resourcesDetails: {
    id: string;
    name: string;
    scanned: boolean;
    os?: OsType | null;
  }[];
  selectedResourceIds?: string[];
  allResourcesSelected?: boolean;
  hasResources?: boolean;
  provider?: CloudAccountCloudProvider;
  resultsCount?: number;
};

export function ResourceListHeader({
  resourcesDetails,
  selectedResourceIds = [],
  allResourcesSelected = false,
  hasResources = false,
  provider,
  resultsCount = 0,
}: Props) {
  const {
    queryState: { field, order },
    updateQueryState,
  } = useQueryState<ResourceQuery>();

  const handleSortDirection = (sortOrder: SortOrder) => {
    updateQueryState({
      order: sortOrder,
    });
  };

  const handleSortField = (sortField: ResourcesSortFields) => {
    updateQueryState({
      field: sortField,
    });
  };

  return (
    <Box boxStyle="lightGrey" spacing="spacing04">
      <FlexContainer justifyContent="space-between">
        <SortSelector<ResourcesSortFields>
          options={sortOptions}
          value={field ?? ResourcesSortFields.Name}
          direction={order ?? SortOrder.Asc}
          onSelect={handleSortField}
          onSortChange={handleSortDirection}
        />
        <Spacer width={theme.spacing.spacing04} />
        <ResourceSearch />
        <Spacer width={theme.spacing.spacing04} />
        <ResourcesFiltersSidebar
          provider={provider}
          resultsCount={resultsCount}
        />
        <Spacer width={theme.spacing.spacing04} />
        <Actions
          size="large"
          {...{ resourcesDetails }}
          selectedResourceIds={selectedResourceIds}
          allResourcesSelected={allResourcesSelected}
          hasResources={hasResources}
        />
      </FlexContainer>
    </Box>
  );
}
